import { capitalize } from '../../ui/utils';
import { formatMoney } from '../../utils';
import { CLICKABLE_FLAGS, lossFlag } from './correctionDiffFields';
import { LossActionsCell } from './LossActionsCell';
import { formatLossDate, toGridDate, toIsoDate } from './lossDates';
import { hasCellError } from './lossValidation';
import { isFieldEditable, isSkyward } from './lossStaging';

/* A row staged for removal this session reads as greyed. Background paints on the row element and shows
   through the transparent cells, so this one works at row level. */
export const lossRowClassRules = {
  'gray-background': (params) => !!params.data && !!params.data._removed,
};

/* Striking an excluded row has to happen per cell, not on the row. AG Grid lays cells out as
   `display: inline-block; position: absolute`, and text-decoration does not propagate into a
   descendant that is atomic-inline or out of flow — so the same rule at row level renders nothing at
   all. A cell decorates its own text regardless of its positioning, which is why this works here.
   Excluded because it is already gone; un-struck the moment a restore is staged, so the click has
   visible effect even though the exclusion only lifts on Save. */
const strikeExcluded = {
  'line-through': (params) => !!params.data && !!params.data.excluded && !params.data._restored,
};

/* Editable columns deliberately carry no valueSetter: AG Grid's own write into the row is what it
   records for undo, so intercepting it would leave Cmd-Z with nothing to reverse. Change is measured
   later by diffing the rows against the snapshot taken when edit mode opened. The Occurred column is
   the exception: it needs one to convert between the row's yyyy-MM-dd string and the Date
   agDateCellEditor works in, and to normalize pasted text — still returning true so the edit is
   recorded for undo like the rest.

   Validation errors arrive as a ref rather than a value: a fresh columnDefs identity makes AG Grid
   reload its columns, which clears the undo stack, so nothing that changes per keystroke may be
   closed over here. The class rules read the ref when the grid re-evaluates them. */
export const buildLossColumns = ({
  editing,
  lossCauses,
  locations,
  cellErrorsRef,
  onToggleRemoval,
  onToggleRevert,
  onToggleRestore,
  onFlagClicked,
}) => {
  const cellHasError = (rowKey, field) => hasCellError((cellErrorsRef && cellErrorsRef.current) || {}, rowKey, field);

  const editableCol = (field, colDef) => ({
    ...colDef,
    editable: (params) =>
      !!editing && !!params.data && !params.data._removed && !params.data._reverted && !params.data.excluded && isFieldEditable(params.data, field),
    cellClassRules: {
      // An excluded or revert-staged row's correctable fields are otherwise indistinguishable from an
      // editable cell. Reverted locks because saveLossChanges lets the revert win over cell edits —
      // an edit typed after staging one would be silently dropped at Save.
      'gray-background': (params) =>
        !!editing && !!params.data && (!!params.data.excluded || !!params.data._reverted || !isFieldEditable(params.data, field)),
      'error-cell': (params) => !!params.data && cellHasError(params.data._key, field),
    },
  });

  // Expense and Recovery share this definition. Indemnity is defined on its own below.
  const moneyCol = (field, headerName) =>
    editableCol(field, {
      headerName,
      field,
      minWidth: 120,
      type: 'rightAligned',
      // dataType drives the shared grid's clipboard coercion, so values pasted from Excel with '$'
      // and ',' arrive as numbers.
      dataType: 'number',
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 2 },
      valueFormatter: (p) => (p.value || p.value === 0 ? formatMoney(p.value, { prefix: '$' }) : '--'),
    });

  const columns = [
    // Pinned so a row stays identifiable once the money and Skyward-context columns scroll the
    // grid sideways — 17 columns is well past one screen.
    editableCol('claimNumber', {
      headerName: 'Claim No.',
      field: 'claimNumber',
      minWidth: 130,
      pinned: 'left',
    }),
    // Columns with no `field` need an explicit colId: it is what AG Grid matches state on when the
    // column defs are replaced, and a positional fallback shifts as soon as the set changes.
    {
      headerName: 'Source',
      colId: 'source',
      minWidth: 110,
      pinned: 'left',
      valueGetter: (p) => (p.data && isSkyward(p.data) ? 'Skyward' : 'Self-reported'),
    },
    /* Sits beside Source because the two answer one question together: where this loss came from and
       whether we have since deviated from it. Buried to the right of the money columns it went unread.
       `corrected` means a durable correction exists; `Review feed` means the feed has since changed
       underneath one, which needs a human, so it keeps the louder treatment. */
    {
      headerName: 'Flags',
      colId: 'flags',
      minWidth: 120,
      valueGetter: (p) => lossFlag(p.data),
      cellStyle: (p) => {
        // A cell that looks clickable but is not (mid-edit, before onCellClicked's own guard) is worse
        // than one that plainly is not, so the pointer cursor drops out while the grid is in edit mode.
        // cellStyle returns plain CSS to AG Grid, outside the theme resolver every other component reads
        // its colors through, so both of these can only be literal hex: the purple is $primary's own
        // value, and the red has no token to borrow.
        if (p.value === 'Review feed') return { color: '#b3261e', fontWeight: 600, ...(editing ? {} : { cursor: 'pointer' }) };
        if (p.value === 'Corrected') return { color: '#5F2994', fontWeight: 600, ...(editing ? {} : { cursor: 'pointer' }) };
        if (p.value === 'Needs indemnity') return { color: '#b3261e', fontWeight: 600 };
        return undefined;
      },
      onCellClicked: (p) => {
        // While editing, the row may carry an unsaved change the modal (fetched fresh from the last
        // saved correction) can't reflect, so it must not open and contradict the cell.
        if (!editing && p.data && CLICKABLE_FLAGS.includes(p.value) && typeof onFlagClicked === 'function') {
          onFlagClicked(p.data);
        }
      },
    },
    editableCol('occurredAt', {
      headerName: 'Occurred',
      colId: 'occurredAt',
      minWidth: 120,
      cellEditor: 'agDateCellEditor',
      valueGetter: (p) => (p.data ? toGridDate(p.data.occurredAt) : null),
      valueFormatter: (p) => (p.data ? formatLossDate(p.data.occurredAt) : '--'),
      // The row holds a yyyy-MM-dd calendar date but agDateCellEditor works in local-midnight Dates, and
      // paste or fill hands in raw text, so the setter normalizes all of them. Returning true is what
      // records the edit for undo.
      valueSetter: (p) => {
        p.data.occurredAt = toIsoDate(p.newValue);
        return true;
      },
    }),
    editableCol('locationId', {
      headerName: 'Location',
      field: 'locationId',
      minWidth: 160,
      cellEditor: 'agSelectCellEditor',
      // The trailing '' is the no-location choice, which the setter turns back into null because '' would
      // fail the locationId::uuid cast.
      cellEditorParams: { values: [...(locations || []).map((loc) => loc.id), ''] },
      /* Reads params.value alone, never params.data: AG Grid calls this with a bare value to label
         each option in the select editor. An id the application no longer carries falls through to
         itself rather than rendering blank, so a stale location is visible instead of looking unset. */
      valueFormatter: (p) => {
        if (!p.value) {
          return 'n/a';
        }
        const match = (locations || []).find((loc) => loc.id === p.value);
        return match ? match.name : p.value;
      },
      valueSetter: (p) => {
        p.data.locationId = p.newValue || null;
        return true;
      },
    }),
    editableCol('status', {
      headerName: 'Status',
      field: 'status',
      minWidth: 100,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['open', 'closed'] },
      valueFormatter: (p) => capitalize(p.value || ''),
    }),
    editableCol('lossCause', {
      headerName: 'Loss Cause',
      field: 'lossCause',
      minWidth: 140,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: (lossCauses || []).map((lc) => lc.lossCause) },
      // A Skyward loss with no cause needs a human to classify it, not a blank cell. 'other' is the
      // marker for a free-text cause and stands in for text the row carries separately. Display only —
      // the editor reads and writes the raw value.
      valueFormatter: (p) => {
        const other = p.data && p.data.lossCauseOther;
        if (!p.value || p.value === 'other') {
          return other || (p.data && isSkyward(p.data) ? 'Unclassified' : '');
        }
        return p.value;
      },
    }),
    editableCol('description', {
      headerName: 'Description',
      field: 'description',
      minWidth: 180,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: { maxLength: 2000, rows: 6, cols: 50 },
    }),
    editableCol('incurred', {
      headerName: 'Indemnity',
      field: 'incurred',
      minWidth: 120,
      type: 'rightAligned',
      // dataType drives the shared grid's clipboard coercion, so values pasted from Excel with
      // '$' and ',' arrive as numbers.
      dataType: 'number',
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { min: 0, precision: 2 },
      valueFormatter: (p) => (p.value || p.value === 0 ? formatMoney(p.value, { prefix: '$' }) : '--'),
    }),
    moneyCol('expense', 'Expense'),
    moneyCol('recovery', 'Recovery'),
    /* Never editable — the server owns the value, and what it sends already follows the rule: our
       formula for a self-reported loss and for a feed loss whose money was corrected, Skyward's own
       figure otherwise. So this previews only what is not saved yet — a self-reported row, whose
       generated total moves with every keystroke, and a feed row whose money the user has started
       changing this session. An untouched feed total is left alone, so opening the editor cannot
       silently restate a claim nobody has touched. */
    {
      headerName: 'Total Incurred',
      colId: 'totalLossAmount',
      minWidth: 150,
      type: 'rightAligned',
      valueGetter: (p) => {
        if (!p.data) {
          return null;
        }
        if (editing && (!isSkyward(p.data) || !!p.data._moneyEdited)) {
          // The generated total is NULL while a self-reported indemnity is, so the preview is too.
          if (!isSkyward(p.data) && (p.data.incurred == null || p.data.incurred === '')) {
            return null;
          }
          const staged = (value) => (value == null || value === '' ? 0 : Number(value));
          return staged(p.data.incurred) - staged(p.data.recovery) + staged(p.data.expense);
        }
        return p.data.totalLossAmount == null ? null : Number(p.data.totalLossAmount);
      },
      valueFormatter: (p) => (p.value || p.value === 0 ? formatMoney(p.value, { prefix: '$' }) : '--'),
    },
    editableCol('carrier', {
      headerName: 'Carrier',
      field: 'carrier',
      minWidth: 130,
    }),
    // Read-only Skyward context. The coverage the money was booked to is not the peril, so these
    // are what an underwriter reads to decide the classification.
    { headerName: 'Coverage', field: 'coverageName', minWidth: 140 },
    { headerName: 'Incident Type', field: 'incidentType', minWidth: 140 },
    { headerName: 'Primary Cause', field: 'primaryCause', minWidth: 140 },
    { headerName: 'Secondary Cause', field: 'secondaryCause', minWidth: 140 },
  ]
    // Every data column carries the strike, in one place so no column can be added later and quietly
    // miss it. Applied before the action column is appended, which must never be struck — a struck
    // Restore button would read as disabled.
    .map((colDef) => ({ ...colDef, cellClassRules: { ...strikeExcluded, ...colDef.cellClassRules } }));

  if (editing) {
    columns.push({
      headerName: 'Actions',
      colId: 'actions',
      minWidth: 140,
      maxWidth: 160,
      // Pinned so Remove/Revert/Restore stay reachable without scrolling past seventeen columns to
      // find them, the way Claim No. and Source stay anchored on the left.
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: LossActionsCell,
      cellRendererParams: { onToggleRemoval, onToggleRevert, onToggleRestore },
    });
  }

  return columns;
};
